import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Wallet, Smartphone, ImagePlus, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Panel, PageHead, Label, StatusBadge, Empty } from "@/components/common";
import { ProductCard, useProducts } from "@/components/ProductCard";
import { MyPlans } from "@/components/Plans";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

const Method = ({ value, current, set, icon: Icon, title, sub, disabled }) => (
  <button type="button" disabled={disabled} onClick={() => set(value)} data-testid={`buy-method-${value}`}
    className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors disabled:opacity-40 ${current === value ? "border-emerald-500 bg-emerald-500/10" : "border-[#1F293D] hover:border-slate-500"}`}>
    <Icon className="mt-0.5 h-4 w-4 text-emerald-400" />
    <span><span className="block text-sm text-white">{title}</span><span className="block text-xs text-slate-500">{sub}</span></span>
  </button>
);

const BuyDialog = ({ product, me, onClose, onDone }) => {
  const api = useApi();
  const canWallet = product && me.user.wallet >= product.price;
  const [method, setMethod] = useState("upi");
  const [address, setAddress] = useState("");
  const [utr, setUtr] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (product) { setMethod(canWallet ? "wallet" : "upi"); setUtr(""); setFile(null); } }, [product, canWallet]);
  if (!product) return null;
  const s = me.settings;
  const uri = `upi://pay?pa=${encodeURIComponent(s.upi_id)}&pn=${encodeURIComponent(s.upi_name)}&am=${product.price}&cu=INR&tn=${encodeURIComponent(`Order - ${product.name}`)}`;
  const ready = address.trim().length >= 10 && (method === "wallet" || utr.length === 12);
  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("product_id", product.id); fd.append("address", address); fd.append("method", method);
    if (method === "upi") { fd.append("utr", utr); if (file) fd.append("screenshot", file); }
    try {
      const r = await api("post", "/me/orders", fd);
      toast.success(r.activated ? "Order placed — your ID is now active!" : method === "wallet" ? "Order placed" : "Order submitted for payment verification");
      onDone(); onClose();
    }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#1F293D] bg-[#111827]" data-testid="buy-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Buy {product.name}</DialogTitle></DialogHeader>
        <p className="font-mono text-2xl text-emerald-400" data-testid="buy-price">{inr(product.price)}</p>
        <div className="grid grid-cols-2 gap-2">
          <Method value="wallet" current={method} set={setMethod} icon={Wallet} title="Pay from wallet" sub={`Balance ${inr(me.user.wallet)}`} disabled={!canWallet} />
          <Method value="upi" current={method} set={setMethod} icon={Smartphone} title="Pay via UPI" sub="Admin verifies UTR" />
        </div>
        {method === "upi" && (
          <div className="space-y-3 rounded-lg border border-[#1F293D] bg-[#0B0F17] p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-white p-2" data-testid="buy-qr"><QRCodeSVG value={uri} size={96} /></div>
              <p className="text-sm text-slate-400">Pay <span className="text-white">{inr(product.price)}</span> to <span className="font-mono text-emerald-400">{s.upi_id}</span>, then enter the UTR.</p>
            </div>
            <Input data-testid="buy-utr-input" inputMode="numeric" maxLength={12} placeholder="12-digit UTR" value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, ""))} className="font-mono tracking-widest" />
            <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-400" data-testid="buy-screenshot-dropzone">
              <ImagePlus className="h-4 w-4 text-emerald-400" /><span className="truncate">{file ? file.name : "Attach screenshot (optional)"}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="buy-screenshot-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size > 5242880) return toast.error("Screenshot must be under 5 MB"); setFile(f || null); }} />
            </label>
          </div>
        )}
        <Textarea data-testid="buy-address-input" placeholder="Full delivery address with PIN code and phone number" value={address} onChange={(e) => setAddress(e.target.value)} />
        <Button onClick={submit} disabled={!ready || busy} className="h-11 rounded-full font-semibold" data-testid="buy-confirm-btn">{busy ? "Placing order…" : method === "wallet" ? `Pay ${inr(product.price)} from wallet` : "Submit order"}</Button>
      </DialogContent>
    </Dialog>
  );
};

const Orders = ({ rows }) => (
  <Panel className="mt-6">
    <Label>My orders</Label>
    <div className="mt-4">
      {!rows ? <p className="text-sm text-slate-500">Loading…</p> : rows.length === 0 ? <Empty testId="orders-empty" text="No orders yet." /> : (
        <Table data-testid="my-orders-table">
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Product</TableHead><TableHead>Paid by</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                <TableCell className="text-xs">{fmtDate(o.created_at)}</TableCell>
                <TableCell className="text-white">{o.product_name}</TableCell>
                <TableCell className="text-xs uppercase text-slate-400">{o.method}{o.utr && <p className="font-mono normal-case text-slate-500">{o.utr}</p>}</TableCell>
                <TableCell className="font-mono">{inr(o.amount)}</TableCell>
                <TableCell><StatusBadge status={o.status} />{o.reason && <p className="mt-1 text-xs text-red-400">{o.reason}</p>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  </Panel>
);

export default function Shop() {
  const api = useApi();
  const { me, refresh } = useOutletContext();
  const products = useProducts();
  const [buying, setBuying] = useState(null);
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => api("get", "/me/orders").then(setOrders), [api]);
  useEffect(() => { load(); }, [load]);
  const active = me.user.status !== "deactivated";
  const needsActivation = ["inactive", "pending"].includes(me.user.status);
  return (
    <div data-testid="shop-page">
      <PageHead label="Shop" title="Buy products">
        <p className="font-mono text-sm text-slate-400">Wallet <span className="text-white">{inr(me.user.wallet)}</span></p>
      </PageHead>
      {!active && <p className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="shop-inactive-note">Your account is deactivated. Please contact the admin.</p>}
      {needsActivation && <p className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-300" data-testid="shop-activation-note">Buy any product of {inr(me.settings.package_price)} or more and your ID is activated automatically once the payment is confirmed.</p>}
      <Panel>
        {!products ? <p className="text-sm text-slate-500">Loading…</p> : products.length === 0 ? <Empty testId="shop-empty" text="No products available right now." /> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products.map((p) => (
              <ProductCard key={p.id} p={p}>
                <Button size="sm" className="mt-4 w-full rounded-full" disabled={!active} onClick={() => setBuying(p)} data-testid={`buy-btn-${p.id}`}><ShoppingBag className="mr-1 h-4 w-4" />Buy now</Button>
              </ProductCard>
            ))}
          </div>
        )}
      </Panel>
      <MyPlans />
      <Orders rows={orders} />
      <BuyDialog product={buying} me={me} onClose={() => setBuying(null)} onDone={() => { load(); refresh(); }} />
    </div>
  );
}
